import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { 
  TreePine, Calendar, Bell, CheckCircle, Leaf,
  Sun, Droplets, AlertTriangle, Phone, Mail,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const arizonaTrees = [
  {
    id: "palo-verde",
    name: "Palo Verde",
    scientific: "Parkinsonia florida",
    trimMonths: ["February", "March"],
    description: "Arizona's state tree with green bark and yellow spring blooms. Trim after flowering for best results.",
    tips: ["Remove crossing branches", "Maintain natural vase shape", "Clean cuts prevent disease"],
    image: "https://images.unsplash.com/photo-1646003071123-c0514172fcb0?w=400&q=80"
  },
  {
    id: "mesquite",
    name: "Mesquite",
    scientific: "Prosopis velutina",
    trimMonths: ["May", "June"],
    description: "Hardy desert tree with feathery foliage. Requires careful pruning to maintain structure.",
    tips: ["Prune when fully leafed", "Remove low-hanging branches", "Avoid heavy pruning"],
    image: "https://images.unsplash.com/photo-1659586554522-51e50bd6bd35?w=400&q=80"
  },
  {
    id: "palm",
    name: "Palm Trees",
    scientific: "Various species",
    trimMonths: ["June", "July", "August"],
    description: "Popular throughout Arizona. Trim dead fronds before monsoon season to prevent storm damage.",
    tips: ["Remove only brown fronds", "Never cut green fronds", "Clean boot bases"],
    image: "https://images.unsplash.com/photo-1698421223097-8ad1d3fc117a?w=400&q=80"
  },
  {
    id: "citrus",
    name: "Citrus Trees",
    scientific: "Citrus spp.",
    trimMonths: ["February", "March", "April"],
    description: "Oranges, lemons, and grapefruit thrive in Arizona. Light pruning encourages fruit production.",
    tips: ["Light pruning only", "Remove water sprouts", "Shape after harvest"],
    image: "https://images.unsplash.com/photo-1583247949334-e07ab70681c5?w=400&q=80"
  },
  {
    id: "olive",
    name: "Olive Trees",
    scientific: "Olea europaea",
    trimMonths: ["February", "March"],
    description: "Mediterranean classic adapted to Arizona. Proper pruning controls pollen and improves shape.",
    tips: ["Thin interior for airflow", "Control height", "Remove suckers"],
    image: "https://images.pexels.com/photos/32704172/pexels-photo-32704172.jpeg?auto=compress&cs=tinysrgb&w=400"
  },
  {
    id: "ash",
    name: "Arizona Ash",
    scientific: "Fraxinus velutina",
    trimMonths: ["November", "December", "January"],
    description: "Fast-growing shade tree. Winter pruning when dormant prevents sap bleeding and stress.",
    tips: ["Prune when dormant", "Remove deadwood", "Maintain strong structure"],
    image: "https://images.pexels.com/photos/34787816/pexels-photo-34787816.jpeg?auto=compress&cs=tinysrgb&w=400"
  }
];

const monthlyCalendar = [
  { month: "January", tasks: ["Ash pruning", "Plan fire safety work"], icon: "❄️" },
  { month: "February", tasks: ["Citrus", "Olive", "Palo Verde prep"], icon: "🌸" },
  { month: "March", tasks: ["Palo Verde", "Citrus completion"], icon: "🌷" },
  { month: "April", tasks: ["Final spring pruning"], icon: "🌼" },
  { month: "May", tasks: ["Mesquite begins"], icon: "☀️" },
  { month: "June", tasks: ["Mesquite", "Palm prep", "Fire safety"], icon: "🔥" },
  { month: "July", tasks: ["Palm trimming peak", "Storm prep"], icon: "🌴" },
  { month: "August", tasks: ["Palm completion", "Monsoon response"], icon: "⛈️" },
  { month: "September", tasks: ["Post-monsoon assessment"], icon: "🍂" },
  { month: "October", tasks: ["Winter prep begins"], icon: "🎃" },
  { month: "November", tasks: ["Ash pruning begins"], icon: "🍁" },
  { month: "December", tasks: ["Dormant season work"], icon: "🎄" },
];

export default function TreeCareBlogPage() {
  const [scrolled, setScrolled] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    name: "",
    email: "",
    tree_type: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleReminderSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/tree-reminder-signup`, reminderForm);
      if (response.data.success) {
        toast.success(response.data.message);
        setReminderForm({ name: "", email: "", tree_type: "" });
      }
    } catch (error) {
      toast.error("Failed to sign up. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Arizona Tree Care Guide | When to Trim Trees | Celebrity Tree Service</title>
        <meta name="description" content="Complete guide to Arizona tree care. Learn the best times to trim Palo Verde, Mesquite, Palm, Citrus and more. Get monthly trimming reminders. Scottsdale & Phoenix." />
        <meta name="keywords" content="Arizona tree care, when to trim trees Arizona, Palo Verde pruning, Palm tree trimming Phoenix, Mesquite tree care, Scottsdale tree service" />
        <link rel="canonical" href="https://paradisevalleytreeservice.com/tree-care-guide" />
      </Helmet>

      <div className="min-h-screen bg-[#F9F9F5]" data-testid="tree-care-page">
        <Navigation scrolled={scrolled} />

        {/* Hero */}
        <section 
          className="relative py-32 lg:py-40"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 22, 40, 0.9), rgba(10, 22, 40, 0.7)), url('https://images.unsplash.com/photo-1646003071039-8c6eadc3e186?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-[#10B981]/20 border border-[#10B981]/40 px-4 py-2 mb-6">
              <Leaf className="w-4 h-4 text-[#10B981]" />
              <span className="text-[#10B981] text-sm font-semibold uppercase tracking-wider">
                Tree Care Guide
              </span>
            </div>
            
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Arizona Tree Care
              <br />
              <span className="text-[#D4A853]">Monthly Guide</span>
            </h1>
            
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Learn when to trim Arizona's most common trees. Sign up for monthly 
              reminders and never miss the optimal pruning window.
            </p>
          </div>
        </section>

        {/* Newsletter/Reminder Signup - Prominent Position */}
        <section className="py-12 bg-gradient-to-r from-[#10B981] to-[#059669]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white p-8 md:p-10 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#D4A853]/20 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-[#D4A853]" />
                </div>
                <div>
                  <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#0A1628]">
                    Get Trimming Reminders
                  </h2>
                  <p className="text-[#64748B] text-sm">
                    We'll remind you when it's time to trim your trees
                  </p>
                </div>
              </div>

              <form onSubmit={handleReminderSubmit} className="grid md:grid-cols-4 gap-4">
                <Input
                  placeholder="Your Name"
                  value={reminderForm.name}
                  onChange={(e) => setReminderForm({ ...reminderForm, name: e.target.value })}
                  required
                  className="border-[#10B981]/30"
                />
                <Input
                  type="email"
                  placeholder="Your Email"
                  value={reminderForm.email}
                  onChange={(e) => setReminderForm({ ...reminderForm, email: e.target.value })}
                  required
                  className="border-[#10B981]/30"
                />
                <Select 
                  value={reminderForm.tree_type}
                  onValueChange={(v) => setReminderForm({ ...reminderForm, tree_type: v })}
                >
                  <SelectTrigger className="border-[#10B981]/30">
                    <SelectValue placeholder="Select Tree Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {arizonaTrees.map(tree => (
                      <SelectItem key={tree.id} value={tree.id}>{tree.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="submit"
                  disabled={isSubmitting || !reminderForm.tree_type}
                  className="bg-gradient-to-r from-[#D4A853] to-[#B8923A] text-[#0A1628] rounded-none font-bold uppercase tracking-wider"
                >
                  {isSubmitting ? "..." : "Get Reminders"}
                </Button>
              </form>
              
              <p className="text-xs text-[#64748B] mt-4 text-center">
                Free monthly reminders. Unsubscribe anytime. We respect your privacy.
              </p>
            </div>
          </div>
        </section>

        {/* Monthly Calendar */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
                Seasonal Planning
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#0A1628] mb-4">
                Year-Round Trimming Calendar
              </h2>
              <p className="text-[#64748B] max-w-2xl mx-auto">
                Plan your tree maintenance throughout the year. Different trees have different optimal pruning windows.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {monthlyCalendar.map((item, idx) => (
                <div 
                  key={idx}
                  className="bg-[#F8F6F1] p-4 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="text-2xl mb-2">{item.icon}</div>
                  <h3 className="font-bold text-[#0A1628] mb-2">{item.month}</h3>
                  <ul className="text-xs text-[#64748B] space-y-1">
                    {item.tasks.map((task, tidx) => (
                      <li key={tidx}>{task}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tree Guide Cards */}
        <section className="py-20 bg-[#FDFCF9]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="font-['Cinzel'] text-[#D4A853] text-xs tracking-[0.3em] uppercase mb-4">
                Arizona Tree Guide
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#0A1628] mb-4">
                Common Arizona Trees
              </h2>
              <p className="text-[#64748B] max-w-2xl mx-auto">
                Learn about the trees that thrive in our desert climate and when to care for them.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {arizonaTrees.map((tree) => (
                <article key={tree.id} className="bg-white overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                  <div 
                    className="h-48 bg-cover bg-center"
                    style={{ backgroundImage: `url('${tree.image}')` }}
                  />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#0A1628]">
                        {tree.name}
                      </h3>
                      <span className="text-xs text-[#64748B] italic">{tree.scientific}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-4">
                      <Calendar className="w-4 h-4 text-[#10B981]" />
                      <span className="text-sm text-[#10B981] font-semibold">
                        Best: {tree.trimMonths.join(", ")}
                      </span>
                    </div>
                    
                    <p className="text-[#64748B] text-sm mb-4">{tree.description}</p>
                    
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-xs font-semibold text-[#0A1628] mb-2">Quick Tips:</p>
                      <ul className="space-y-1">
                        {tree.tips.map((tip, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-[#64748B]">
                            <CheckCircle className="w-3 h-3 text-[#10B981] flex-shrink-0 mt-0.5" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* General Tips */}
        <section className="py-20 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-4xl font-bold text-white mb-4">
                Arizona Tree Care Tips
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Sun, title: "Avoid Extreme Heat", desc: "Never prune when temps exceed 100°F. Heat stress can damage trees." },
                { icon: Droplets, title: "Water Before & After", desc: "Deep watering helps trees recover from pruning stress." },
                { icon: TreePine, title: "25% Rule", desc: "Never remove more than 25% of a tree's canopy in one session." },
                { icon: AlertTriangle, title: "Fire Safety", desc: "Create defensible space before summer. Remove dead wood and debris." },
              ].map((tip, idx) => (
                <div key={idx} className="bg-white/10 p-6 text-center">
                  <tip.icon className="w-10 h-10 text-[#10B981] mx-auto mb-4" />
                  <h3 className="font-bold text-white mb-2">{tip.title}</h3>
                  <p className="text-white/70 text-sm">{tip.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-[#D4A853]/20 to-[#D4A853]/5">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#0A1628] mb-4">
              Need Professional Tree Care?
            </h2>
            <p className="text-[#64748B] mb-8">
              Our ISA Certified Arborists are ready to help with all your tree care needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-8 py-5 uppercase tracking-wider font-bold"
              >
                <a href="tel:4803517775" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Call (480) 351-7775
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-[#0A1628] text-[#0A1628] rounded-none px-8 py-5 uppercase tracking-wider font-bold"
              >
                <a href="/#contact">Request Free Estimate</a>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
